const chapter = {
  id: 'redox-reactions',
  slug: 'redox-reactions',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Redox Reactions',
        hindi: 'अपचयोपचय अभिक्रियाएँ',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 1-2 प्रश्न',
  },
  syllabusTags: ['Physical Chemistry', 'Inorganic Chemistry', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'concepts',
      title: {
        english: 'Three Views of Oxidation–Reduction',
        hindi: 'ऑक्सीकरण-अवकरण के तीन दृष्टिकोण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Classical view**: oxidation = addition of O / removal of H. Reduction = addition of H / removal of O.',
        hindi: '**शास्त्रीय दृष्टिकोण**: ऑक्सीकरण = O का योग / H का निष्कासन। कमी = H का योग / O का निष्कासन।',
            },
            {
              english: '**Electronic view**: oxidation = **loss** of electrons (LEO). Reduction = **gain** of electrons (GER).',
        hindi: '**इलेक्ट्रॉनिक दृश्य**: ऑक्सीकरण = **इलेक्ट्रॉनों की हानि** (LEO)। इलेक्ट्रॉनों की कमी = **लाभ** (जीईआर)।',
            },
            {
              english: '**Oxidation-number view**: oxidation = increase in oxidation number; reduction = decrease.',
        hindi: '**ऑक्सीकरण-संख्या दृश्य**: ऑक्सीकरण = increase in ऑक्सीकरण number; अपचयन = decrease.',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Oxidising / Reducing agent',
        hindi: 'ऑक्सीकरण / कम करने वाला एजेंट',
          },
          body: {
            english: 'Oxidising agent (oxidant): species that **gains electrons** and is itself reduced. Reducing agent (reductant): species that **loses electrons** and is itself oxidised.',
        hindi: 'ऑक्सीकारक एजेंट (ऑक्सीडेंट): ऐसी प्रजातियां जो **इलेक्ट्रॉन प्राप्त करती हैं** और स्वयं कम हो जाती हैं। अपचायक (रिडक्टेंट): ऐसी प्रजातियां जो **इलेक्ट्रॉन खो देती हैं** और स्वयं ऑक्सीकृत हो जाती हैं।',
          },
        },
        {
          type: 'tip',
          text: {
            english: 'Memory aid: **OIL RIG** — Oxidation Is Loss, Reduction Is Gain (of electrons).',
        hindi: 'स्मृति सहायता: **ऑयल रिग** - ऑक्सीकरण हानि है, न्यूनीकरण लाभ है (इलेक्ट्रॉनों का)।',
          },
        },
      ],
    },
    {
      id: 'oxidation-numbers',
      title: {
        english: 'Rules for Oxidation Number',
        hindi: 'ऑक्सीकरण संख्या के नियम',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Free element (in any allotrope): **0**.',
        hindi: 'मुफ़्त तत्व (किसी भी एलोट्रोप में): **0**।',
            },
            {
              english: 'Monatomic ion: equal to its charge. Na⁺ = +1, S²⁻ = −2.',
        hindi: 'मोनोएटोमिक आयन: इसके आवेश के बराबर। Na⁺ = +1, S²⁻ = −2.',
            },
            {
              english: 'Sum of oxidation numbers in a neutral compound = 0; in an ion = its charge.',
        hindi: 'एक तटस्थ यौगिक = 0 में ऑक्सीकरण संख्याओं का योग; ion = its charge में।',
            },
            {
              english: '**H** = +1 in compounds (except metal hydrides like NaH, where it is −1).',
        hindi: '**H** = यौगिकों में +1 (NaH जैसे धातु हाइड्राइड को छोड़कर, जहां यह −1 है)।',
            },
            {
              english: '**O** = −2 (except peroxides −1, superoxides −1/2, OF₂ +2, O₂F₂ +1).',
        hindi: '**O** = −2 (पेरोक्साइड्स −1, सुपरऑक्साइड्स −1/2, OF₂ +2, O₂F₂ +1 को छोड़कर)।',
            },
            {
              english: '**F** = −1 always; other halogens = −1 except when bonded to O or to a more electronegative halogen.',
        hindi: '**एफ** = −1 हमेशा; अन्य हैलोजन = −1, सिवाय इसके कि जब O या अधिक विद्युत ऋणात्मक हैलोजन से बंधा हो।',
            },
            {
              english: 'Group 1 metals = +1, Group 2 = +2.',
        hindi: 'समूह 1 की धातुएँ = +1, समूह 2 = +2।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Oxidation number of Mn in $KMnO_4$ and Cr in $K_2Cr_2O_7$?',
        hindi: '$KMnO_4$ में Mn और $K_2Cr_2O_7$ में Cr की ऑक्सीकरण संख्या?',
          },
          solution: {
            english: `KMnO₄: $+1 + x + 4(-2) = 0 \\Rightarrow x = +7$.
K₂Cr₂O₇: $2(+1) + 2x + 7(-2) = 0 \\Rightarrow x = +6$.`,
            hindi: 'KMnO₄: $+1 + x + 4(-2) = 0 \\Rightarrow x = +7$। K₂Cr₂O₇: $2(+1) + 2x + 7(-2) = 0 \\Rightarrow x = +6$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Average vs individual oxidation numbers can differ in compounds with non-equivalent atoms (e.g. average S in Na₂S₂O₃ = +2, but two sulphurs are +5 and −1).',
        hindi: 'औसत बनाम व्यक्तिगत ऑक्सीकरण संख्या गैर-समतुल्य परमाणुओं वाले यौगिकों में भिन्न हो सकती है (उदाहरण के लिए Na₂S₂O₃ = +2 में औसत S, लेकिन दो सल्फर +5 और -1 हैं)।',
            },
            {
              english: 'Fractional oxidation numbers (e.g. Fe in Fe₃O₄ = +8/3) are averages of integral values (FeO + Fe₂O₃).',
        hindi: 'भिन्नात्मक ऑक्सीकरण संख्याएँ (उदाहरण के लिए Fe₃O₄ = +8/3 में Fe) अभिन्न मान (FeO + Fe₂O₃) के औसत हैं।',
            },
          ],
        },
      ],
    },
    {
      id: 'types',
      title: {
        english: 'Types of Redox Reactions',
        hindi: 'रेडॉक्स अभिक्रियाओं के प्रकार',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Combination**: A + B → AB, when at least one is an element. e.g. C + O₂ → CO₂.',
        hindi: '**संयोजन**: ए + बी → एबी, जब कम से कम एक तत्व हो। जैसे C + O₂ → CO₂.',
            },
            {
              english: '**Decomposition**: AB → A + B; reverse of combination. e.g. 2KClO₃ → 2KCl + 3O₂.',
        hindi: '**अपघटन**: एबी → ए + बी; संयोजन का उलटा. जैसे 2KClO₃ → 2KCl + 3O₂.',
            },
            {
              english: '**Displacement** (one element displaces another): metal–metal (Zn + CuSO₄ → ZnSO₄ + Cu), non-metal–non-metal (Cl₂ + 2KBr → 2KCl + Br₂). Driven by reactivity series / EN.',
        hindi: '**विस्थापन** (एक तत्व दूसरे को विस्थापित करता है): धातु-धातु (Zn + CuSO₄ → ZnSO₄ + Cu), अधातु-अधातु (Cl₂ + 2KBr → 2KCl + Br₂)। प्रतिक्रियाशीलता श्रृंखला / EN द्वारा संचालित।',
            },
            {
              english: '**Disproportionation**: same element is **simultaneously oxidised and reduced**. e.g. $2H_2O_2 \\to 2H_2O + O_2$ (O: −1 → −2 and 0); $Cl_2 + 2OH^- \\to Cl^- + ClO^- + H_2O$.',
        hindi: '**असमानुपातन**: एक ही तत्व **एक साथ ऑक्सीकृत और अपचित** होता है। जैसे $2H_2O_2 \\to 2H_2O + O_2$ (O: −1 → −2 और 0); $Cl_2 + 2OH^- \\to Cl^- + ClO^- + H_2O$.',
            },
            {
              english: '**Comproportionation** (reverse of disproportionation): two species of same element in different oxidation states form one common product.',
        hindi: '**अनुपातन** (असमानुपातन का विपरीत): विभिन्न ऑक्सीकरण अवस्थाओं में एक ही तत्व की दो प्रजातियाँ एक सामान्य उत्पाद बनाती हैं।',
            },
          ],
        },
      ],
    },
    {
      id: 'balancing',
      title: {
        english: 'Balancing Redox Equations',
        hindi: 'रेडॉक्स समीकरणों को सन्तुलित करना',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Two systematic methods: (a) **oxidation-number method** and (b) **ion-electron / half-reaction method** (works for acidic and basic media).',
        hindi: 'दो व्यवस्थित विधियाँ: (ए) **ऑक्सीकरण-संख्या विधि** और (बी) **आयन-इलेक्ट्रॉन / अर्ध-प्रतिक्रिया विधि** (अम्लीय और बुनियादी मीडिया के लिए काम करती है)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Step 1: split into oxidation and reduction half-reactions.',
        hindi: 'चरण 1: ऑक्सीकरण और अपचयन अर्ध-प्रतिक्रियाओं में विभाजित करें।',
            },
            {
              english: 'Step 2: balance atoms other than O and H.',
        hindi: 'चरण 2: O और H के अलावा अन्य परमाणुओं को संतुलित करें।',
            },
            {
              english: 'Step 3: balance O by adding H₂O; balance H by adding H⁺.',
        hindi: 'चरण 3: H₂O जोड़कर O को संतुलित करें; H⁺ जोड़कर H को संतुलित करें।',
            },
            {
              english: 'Step 4: balance charge by adding electrons.',
        hindi: 'चरण 4: इलेक्ट्रॉन जोड़कर चार्ज को संतुलित करें।',
            },
            {
              english: 'Step 5: equalise electrons and add the half-reactions.',
        hindi: 'चरण 5: इलेक्ट्रॉनों को बराबर करें और आधी-प्रतिक्रियाएँ जोड़ें। **बुनियादी माध्यम** के लिए: H⁺ की संख्या के बराबर दोनों तरफ OH⁻ जोड़ें, जोड़ों को H₂O में संयोजित करें।',
            },
            {
              english: 'For **basic medium**: add OH⁻ to both sides equal to number of H⁺, combine pairs into H₂O.',
        hindi: '**मूल माध्यम** के लिए: H⁺ की संख्या के बराबर दोनों तरफ OH⁻ जोड़ें, जोड़ों को H₂O में संयोजित करें।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Balance $MnO_4^- + Fe^{2+} \\to Mn^{2+} + Fe^{3+}$ in acidic medium.',
        hindi: 'अम्लीय माध्यम में $MnO_4^- + Fe^{2+} \\to Mn^{2+} + Fe^{3+}$ को संतुलित करें।',
          },
          solution: {
            english: `Reduction: $MnO_4^- + 8H^+ + 5e^- \\to Mn^{2+} + 4H_2O$.
Oxidation: $Fe^{2+} \\to Fe^{3+} + e^-$  (× 5).
Add: $MnO_4^- + 5Fe^{2+} + 8H^+ \\to Mn^{2+} + 5Fe^{3+} + 4H_2O$.`,
            hindi: 'कमी: $MnO_4^- + 8H^+ + 5e^- \\to Mn^{2+} + 4H_2O$। ऑक्सीकरण: $Fe^{2+} \\to Fe^{3+} + e^-$ (× 5)। जोड़ें: $MnO_4^- + 5Fe^{2+} + 8H^+ \\to Mn^{2+} + 5Fe^{3+} + 4H_2O$.',
          },
        },
      ],
    },
    {
      id: 'electrochem-link',
      title: {
        english: 'Redox & Electrochemistry — Electrode Potentials',
        hindi: 'रेडॉक्स और इलेक्ट्रोकैमिस्ट्री - इलेक्ट्रोड क्षमताएं',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'In a galvanic cell, **oxidation at anode**, **reduction at cathode**. Conventional cell notation: anode | anode soln || cathode soln | cathode.',
        hindi: 'एक गैल्वेनिक सेल में, **एनोड पर ऑक्सीकरण**, **कैथोड पर कमी**। पारंपरिक सेल संकेतन: एनोड | एनोड सोलन || कैथोड सोलन | कैथोड.',
            },
            {
              english: 'Standard hydrogen electrode (SHE): $E^\\circ = 0$ V (reference). $H_2$ gas at 1 bar over Pt in 1 M H⁺.',
        hindi: 'मानक हाइड्रोजन इलेक्ट्रोड (SHE): $E^\\circ = 0$ V (संदर्भ)। $H_2$ 1 M H⁺ में Pt के ऊपर 1 बार गैस।',
            },
            {
              english: 'Standard reduction potential $E^\\circ$ measures **tendency to be reduced**. More positive ⇒ stronger oxidising agent.',
        hindi: 'मानक कमी क्षमता $E^\\circ$ **कम होने की प्रवृत्ति** को मापती है। अधिक सकारात्मक ⇒ मजबूत ऑक्सीकरण एजेंट।',
            },
            {
              english: 'EMF of cell: $E^\\circ_{\\text{cell}} = E^\\circ_{\\text{cathode}} - E^\\circ_{\\text{anode}}$ (both as reduction potentials).',
        hindi: 'सेल का ईएमएफ: $E^\\circ_{\\text{cell}} = E^\\circ_{\\text{cathode}} - E^\\circ_{\\text{anode}}$ (दोनों कमी क्षमता के रूप में)।',
            },
            {
              english: 'Reactivity / activity series of metals = arrangement in increasing $E^\\circ$.',
        hindi: 'धातुओं की प्रतिक्रियाशीलता/सक्रियता श्रृंखला = बढ़ाने में व्यवस्था $E^\\circ$.',
            },
            {
              english: '$\\Delta G^\\circ = -nFE^\\circ$. Spontaneous if $E^\\circ_{\\text{cell}} > 0$.',
        hindi: '$\\Delta G^\\circ = -nFE^\\circ$. सहज यदि $E^\\circ_{\\text{cell}} > 0$.',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'A metal with more negative $E^\\circ$ displaces metals with less negative $E^\\circ$ from their salts (e.g. Zn displaces Cu).',
        hindi: 'अधिक ऋणात्मक $E^\\circ$ वाली धातु कम ऋणात्मक $E^\\circ$ वाली धातुओं को उनके लवणों से विस्थापित कर देती है (जैसे Zn Cu को विस्थापित कर देता है)।',
          },
        },
      ],
    },
    {
      id: 'titrations',
      title: {
        english: 'Redox Titrations & Equivalent Concept',
        hindi: 'रेडॉक्स अनुमापन और तुल्यांक अवधारणा',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**$n$-factor** of an oxidising / reducing agent = number of electrons gained / lost per formula unit.',
        hindi: 'ऑक्सीकरण / कम करने वाले एजेंट का **$n$-कारक** = प्रति सूत्र इकाई प्राप्त / खोए गए इलेक्ट्रॉनों की संख्या।',
            },
            {
              english: 'Equivalent mass = (molar mass) / ($n$-factor).',
        hindi: 'समतुल्य द्रव्यमान = (दाढ़ द्रव्यमान) / ($n$-कारक)।',
            },
            {
              english: 'Examples: KMnO₄ (acidic) $n=5$; (neutral / faintly basic) $n=3$; (strongly basic) $n=1$. K₂Cr₂O₇ (acidic) $n=6$. Hypo (Na₂S₂O₃ in iodometry) $n=1$.',
        hindi: 'उदाहरण: KMnO₄ (अम्लीय) $n=5$; (तटस्थ / हल्का बुनियादी) $n=3$; (दृढ़ता से बुनियादी) $n=1$। K₂Cr₂O₇ (अम्लीय) $n=6$। हाइपो (आयोडोमेट्री में Na₂S₂O₃) $n=1$। **परमैंगनेट अनुमापन** (पतला H₂SO₄ में): MnO₄⁻ स्व-संकेतक (गुलाबी → रंगहीन) है। **आयोडोमेट्रिक / आयोडिमेट्रिक**: स्टार्च संकेतक → अंत में नीला। $n=5$, $n=3$, ऑक्सीकरण एजेंटों के लिए उपयोग किया जाता है।',
            },
            {
              english: '**Permanganate titration** (in dil H₂SO₄): MnO₄⁻ is self-indicator (pink → colourless).',
        hindi: '**परमैंगनेट अनुमापन** (पतला H₂SO₄ में): MnO₄⁻ स्व-संकेतक (गुलाबी → रंगहीन) है।',
            },
            {
              english: '**Iodometric / iodimetric**: starch indicator → blue at end. Used for $Cu^{2+}$, $Cl_2$, oxidising agents.',
        hindi: '**आयोडोमेट्रिक / आयोडिमेट्रिक**: स्टार्च संकेतक → अंत में नीला। $Cu^{2+}$, $Cl_2$, ऑक्सीकरण एजेंटों के लिए उपयोग किया जाता है।',
            },
            {
              english: 'Equivalence: $N_1 V_1 = N_2 V_2$ (same as $n_1 M_1 V_1 = n_2 M_2 V_2$).',
        hindi: 'समतुल्यता: $N_1 V_1 = N_2 V_2$ ($n_1 M_1 V_1 = n_2 M_2 V_2$ के समान)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Ox.\\,No.\\,(Mn in KMnO}_4) = +7',
      desc: {
        english: 'Sample assignment',
        hindi: 'नमूना असाइनमेंट',
      },
    },
    {
      latex: 'E^\\circ_{\\text{cell}} = E^\\circ_{\\text{cat}} - E^\\circ_{\\text{an}}',
      desc: {
        english: 'Cell EMF',
        hindi: 'सेल ईएमएफ',
      },
    },
    {
      latex: '\\Delta G^\\circ = -nFE^\\circ',
      desc: {
        english: 'Free energy ↔ EMF',
        hindi: 'मुफ्त ऊर्जा ↔ ईएमएफ',
      },
    },
    {
      latex: '\\text{Eq.\\,wt} = \\dfrac{M}{n\\text{-factor}}',
      desc: {
        english: 'Equivalent mass',
        hindi: 'समतुल्य द्रव्यमान',
      },
    },
    {
      latex: 'n_{\\text{KMnO}_4}^{\\text{acidic}} = 5',
      desc: {
        english: 'Permanganate (acid)',
        hindi: 'परमैंगनेट (एसिड)',
      },
    },
    {
      latex: 'n_{\\text{K}_2\\text{Cr}_2\\text{O}_7} = 6',
      desc: {
        english: 'Dichromate',
        hindi: 'डाइक्रोमेट',
      },
    },
    {
      latex: 'N_1 V_1 = N_2 V_2',
      desc: {
        english: 'Titration equivalence',
        hindi: 'अनुमापन तुल्यता',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Disproportion',
      problem: {
        english: 'Which undergoes disproportionation in alkaline medium?',
        hindi: 'क्षारीय माध्यम में कौन सा अनुपातहीन हो जाता है?',
      },
      answer: {
        english: 'Cl₂',
        hindi: 'सीएल₂',
      },
      solution: {
        english: '$Cl_2 + 2OH^- \\to Cl^- + ClO^- + H_2O$.',
        hindi: '$Cl_2 + 2OH^- \\to Cl^- + ClO^- + H_2O$.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Ox. no.',
      problem: {
        english: 'Oxidation state of S in $H_2 S_2 O_8$ (Marshall\'s acid)?',
        hindi: '$H_2 S_2 O_8$ (मार्शल एसिड) में S की ऑक्सीकरण अवस्था?',
      },
      answer: {
        english: '+6',
        hindi: '+6',
      },
      solution: {
        english: 'Two peroxide-O (−1) account for −2; rest standard.',
        hindi: 'दो पेरोक्साइड-O (−1) −2 के लिए जिम्मेदार हैं; बाकी मानक.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Equivalents',
      problem: {
        english: 'How many moles of FeSO₄ react with 1 mol KMnO₄ in acidic medium?',
        hindi: 'अम्लीय माध्यम में FeSO₄ के कितने मोल 1 mol KMnO₄ के साथ प्रतिक्रिया करते हैं?',
      },
      answer: {
        english: '5',
        hindi: '5',
      },
      solution: {
        english: '$n$-factor ratio 5 : 1.',
        hindi: '$n$-कारक अनुपात 5:1.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Cell',
      problem: {
        english: 'Given $E^\\circ(\\text{Zn}^{2+}/\\text{Zn}) = -0.76$, $E^\\circ(\\text{Cu}^{2+}/\\text{Cu}) = +0.34$ V. EMF of Zn|Zn²⁺||Cu²⁺|Cu?',
        hindi: 'दिया गया $E^\\circ(\\text{Zn}^{2+}/\\text{Zn}) = -0.76$, $E^\\circ(\\text{Cu}^{2+}/\\text{Cu}) = +0.34$ V. Zn|Zn²⁺||Cu²⁺|Cu का EMF?',
      },
      answer: {
        english: '+1.10 V',
        hindi: '+1.10 वी',
      },
      solution: {
        english: '$0.34 - (-0.76) = 1.10$.',
        hindi: '$0.34 - (-0.76) = 1.10$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Balancing',
      problem: {
        english: 'Coefficient of H⁺ when $Cr_2O_7^{2-} + Fe^{2+} \\to Cr^{3+} + Fe^{3+}$ is balanced (acidic)?',
        hindi: 'H⁺ का गुणांक जब $Cr_2O_7^{2-} + Fe^{2+} \\to Cr^{3+} + Fe^{3+}$ संतुलित (अम्लीय) होता है?',
      },
      answer: {
        english: '14',
        hindi: '14',
      },
      solution: {
        english: 'Standard balanced eqn: $Cr_2O_7^{2-} + 6Fe^{2+} + 14H^+ \\to 2Cr^{3+} + 6Fe^{3+} + 7H_2O$.',
        hindi: 'मानक संतुलित समीकरण: $Cr_2O_7^{2-} + 6Fe^{2+} + 14H^+ \\to 2Cr^{3+} + 6Fe^{3+} + 7H_2O$.',
      },
    },
  ],
  next: {
    slug: 'hydrogen',
    title: 'Hydrogen',
  },
};

export default chapter;
